/** Keep shape stroke above normal layer stacking when content overlaps the shape. */
export const SHAPE_STROKE_Z_BIAS = 100000;

export const getShapeStrokeZIndex = (zIndex) =>
  (Number(zIndex) || 0) + SHAPE_STROKE_Z_BIAS;

export const hasVisibleShapeBorder = (border) => {
  if (!border || typeof border !== "string") return false;
  const pxMatch = border.match(/(\d+(?:\.\d+)?)\s*px/i);
  if (pxMatch) return Number(pxMatch[1]) > 0;
  return !/\b0\b/.test(border);
};

/** Derive a fill-less SVG data-URI so only the stroke paints as a frame. */
export const buildStrokeOnlySvgSrc = (src) => {
  if (!src || typeof src !== "string") return src;
  return src
    .replace(/fill%3D%22%23[0-9A-Fa-f]{3,8}%22/gi, "fill%3D%22none%22")
    .replace(/fill%3D%22(?!none)[^%"]*%22/gi, "fill%3D%22none%22")
    .replace(/fill="#[0-9A-Fa-f]{3,8}"/gi, 'fill="none"')
    .replace(/fill="(?!none)[^"]*"/gi, 'fill="none"')
    .replace(/fill%3D%27%23[0-9A-Fa-f]{3,8}%27/gi, "fill%3D%27none%27")
    .replace(/fill%3D%27(?!none)[^%']*%27/gi, "fill%3D%27none%27");
};

/** Derive a stroke-less SVG data-URI so the base layer is fill only. */
export const buildFillOnlySvgSrc = (src) => {
  if (!src || typeof src !== "string") return src;
  return src
    .replace(/stroke-width%3D%22[^%"]*%22/gi, "stroke-width%3D%220%22")
    .replace(/stroke-width="[^"]*"/gi, 'stroke-width="0"')
    .replace(/stroke-width%3D%27[^%']*%27/gi, "stroke-width%3D%270%27")
    .replace(/stroke%3D%22[^%"]*%22/gi, "stroke%3D%22none%22")
    .replace(/stroke="[^"]*"/gi, 'stroke="none"')
    .replace(/stroke%3D%27[^%']*%27/gi, "stroke%3D%27none%27");
};

const SHAPE_SVG_SOURCE_KEYS = {
  triangleShape: "triImage",
  polygonShape: "pentImage",
  hexagonShape: "hexImage",
};

export const getShapeSvgSourceKey = (addContent) =>
  SHAPE_SVG_SOURCE_KEYS[addContent] || "";

export const getShapeSvgSource = (items) => {
  const key = getShapeSvgSourceKey(items?.textComponentType?.addContent);
  return key ? items?.[key] || "" : "";
};

export const isSvgShapeType = (addContent) =>
  Boolean(getShapeSvgSourceKey(addContent));

const toHexChannel = (channel) => {
  const numeric = Math.round(Number(channel));
  if (!Number.isFinite(numeric)) return "";
  return Math.min(255, Math.max(0, numeric)).toString(16).padStart(2, "0");
};

/**
 * SVG presentation attributes need a hex literal, and the shape colours reach
 * here as picker objects, rgba strings or hex. Returning "" rather than a
 * partial value matters: an empty fill/stroke renders the shape as nothing
 * while its layer is still on the canvas.
 */
export const toSvgHexColor = (color) => {
  if (!color) return "";

  if (typeof color === "object") {
    const channels = [color.r, color.g, color.b].map(toHexChannel);
    return channels.every(Boolean) ? `#${channels.join("")}` : "";
  }

  const value = String(color);
  const rgbMatch = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const channels = rgbMatch.slice(1, 4).map(toHexChannel);
    return channels.every(Boolean) ? `#${channels.join("")}` : "";
  }

  const hexMatch = value.match(/#([0-9a-f]{6}|[0-9a-f]{3})\b/i);
  if (!hexMatch) return "";
  const hex = hexMatch[1].toLowerCase();
  return hex.length === 3
    ? `#${hex
        .split("")
        .map((channel) => channel + channel)
        .join("")}`
    : `#${hex}`;
};

/** Shape SVGs are data URIs that may carry attributes encoded or verbatim. */
export const setSvgAttributeInSrc = (src, attribute, value) => {
  if (!src || typeof src !== "string" || value === "" || value == null) {
    return src;
  }
  const encoded = new RegExp(`${attribute}%3D%22(?:(?!%22)[\\s\\S])*?%22`, "i");
  if (encoded.test(src)) {
    return src.replace(
      encoded,
      `${attribute}%3D%22${encodeURIComponent(value)}%22`,
    );
  }
  return src.replace(new RegExp(`${attribute}="[^"]*"`, "i"), `${attribute}="${value}"`);
};

export const isCssBorderShapeType = (addContent) =>
  addContent === "rectangleShape" || addContent === "circleShape";

/** Elevated border overlay is only needed when the shape is canvas-sized. */
export const isShapeMatchingCanvasSize = (styles, canvas) => {
  if (!styles || !canvas) return false;
  const shapeWidth = Number(styles.width);
  const shapeHeight = Number(styles.height);
  const canvasWidth = Number(canvas.width);
  const canvasHeight = Number(canvas.height);
  if (
    ![shapeWidth, shapeHeight, canvasWidth, canvasHeight].every(Number.isFinite)
  ) {
    return false;
  }
  if (canvasWidth <= 0 || canvasHeight <= 0) return false;
  return (
    Math.round(shapeWidth) === Math.round(canvasWidth) &&
    Math.round(shapeHeight) === Math.round(canvasHeight)
  );
};
